import { useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  EventName,
  Request,
  useBridgeAPI,
  useBridgeState,
  useWebViewPostMessage,
} from '@digitaz/bridge-sdk-client';
import {
  CreatePaymentResponse,
  Invoice,
  PaymentMethod,
  PaymentService,
  PaymentStatus,
} from '@/api/types/payments.types';
import { MutationError } from '@/api/types/common.types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useCreatePayment } from '@/hooks/useCreatePayment';
import { useNewWindow } from '@/hooks/useNewWindow';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { useResponsive } from '@/hooks/useResponsive';
import {
  getPaymentYigimTemplateGroup,
  getRefetchDelay,
} from '@/api/helpers/payments.helpers';
import { useTerminalPayment } from '@/components/Payment/useTerminalPayment';
import { usePaymentStatusModalStore } from '@/store/usePaymentStatusModalStore';
import { usePostMessage } from '@/hooks/usePostMessage';
import { PaymentStatusParams } from '@/bridge/types/payment';
import { buildWebViewPaymentData } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.helpers';
import { sleep } from '@/utils/helpers';
import { ROUTES } from '@/constants/routes';

export type UseInvoicePaymentParams = {
  amount: number;
  getInvoice: () => Promise<PayableInvoice | null>;
  isAppleGooglePayEnabled?: boolean;
  isOpen: boolean;
  onBalanceTopUpSuccess?: () => void;
  onClose: () => void;
  onSuccess: () => void;
  paymentMethods?: PaymentMethod[];
  serviceName: string;
};

export type PayableInvoice = Pick<Invoice, 'amount' | 'id'> & {
  legacyResourceId: Invoice['legacyResourceId'] | string;
};

export function useInvoicePayment({
  amount,
  getInvoice,
  isAppleGooglePayEnabled,
  isOpen,
  onBalanceTopUpSuccess,
  onClose,
  onSuccess,
  paymentMethods,
  serviceName,
}: UseInvoicePaymentParams) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const source = usePlatformSource();
  const { isDesktop } = useResponsive();
  const { isWebView } = useBridgeState();
  const bridgeAPI = useBridgeAPI();
  const shouldSkipNextPaymentFinished = useRef(false);
  const paymentInvoice = useRef<PayableInvoice | null>(null);
  const [payment, setPayment] = useState(PaymentMethod.Card);
  const [cardId, setCardId] = useState<string>();
  const [terminalInvoice, setTerminalInvoice] = useState<PayableInvoice | null>(
    null
  );
  const [createPaymentError, setCreatePaymentError] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isNotEnoughFundsOpen, toggleNotEnoughFunds] = useToggleOpenState();
  const [isAddBalanceOpen, toggleAddBalance] = useToggleOpenState();
  const [isTooManyRequestsOpen, toggleTooManyRequests] = useToggleOpenState();
  const {
    newWindow: paymentWindow,
    close: closePaymentWindow,
    open: openPaymentWindow,
  } = useNewWindow();
  const [openWebViewPaymentModal, setIsSuccessOpen] =
    usePaymentStatusModalStore(state => [
      state.openWebViewPaymentModal,
      state.setIsSuccessPaymentModalOpen,
    ]);
  const {
    createPayment,
    loading: isPaymentLoading,
    paymentProvider,
  } = useCreatePayment({
    onSuccess: handleCreatePaymentSuccess,
    onError: handleCreatePaymentError,
    onRedirect: handleCreatePaymentRedirect,
  });
  const {
    createTerminalPayment,
    isCreateTerminalPaymentLoading,
    isTerminal,
    isTerminalOpen,
    toggleTerminal,
  } = useTerminalPayment({ payment });

  const isProcessingPayment =
    isPaymentLoading || isCreateTerminalPaymentLoading;

  function refetchPaymentData() {
    return client.refetchQueries({ include: 'active' }).catch(() => undefined);
  }

  function handleBalanceTopUpSuccess() {
    shouldSkipNextPaymentFinished.current = true;
    onBalanceTopUpSuccess?.();
  }

  function clearPaymentInvoice() {
    paymentInvoice.current = null;
  }

  function clearCreatePaymentError() {
    setCreatePaymentError('');
    if (!isDuplicate) return;

    setIsDuplicate(false);
    onClose();
  }

  function finishPayment() {
    clearPaymentInvoice();
    onClose();
    setIsSuccessOpen(true);
    refetchPaymentData();
    onSuccess();
  }

  function showPaymentReceipt(paymentLegacyId?: string) {
    if (!paymentWindow || !paymentLegacyId) {
      closePaymentWindow();
      return;
    }

    const searchParams = new URLSearchParams({ id: paymentLegacyId });
    paymentWindow.location.href = `${ROUTES.walletFinish}?${searchParams}`;
  }

  function handleCreatePaymentError(
    status: PaymentStatus,
    errors?: MutationError[]
  ) {
    closePaymentWindow();
    if (errors?.some(error => error.code === 'insufficient_funds')) {
      toggleNotEnoughFunds(true);
      return;
    }
    if (errors?.some(error => error.code === '429')) {
      toggleTooManyRequests(true);
      return;
    }
    if (!errors?.length || status !== PaymentStatus.Failure) return;

    const duplicated = errors[0].code === '666';
    setIsDuplicate(duplicated);
    if (isWebView) {
      if (duplicated) {
        clearPaymentInvoice();
        onClose();
      }
      openWebViewPaymentModal({
        status: duplicated ? PaymentStatus.Duplicate : PaymentStatus.Failure,
      });
      return;
    }
    setCreatePaymentError(errors[0].message);
    clearPaymentInvoice();
  }

  function handleCreatePaymentRedirect(redirectUrl: string) {
    if (isWebView) {
      bridgeAPI.openPayment(redirectUrl);
      return;
    }
    if (paymentWindow) paymentWindow.location.href = redirectUrl;
  }

  function handleCreatePaymentSuccess(data: CreatePaymentResponse) {
    if (!isWebView) {
      showPaymentReceipt(data.createPayment.entity.paymentLegacyId);
      finishPayment();
      return;
    }

    const { paymentDate, paymentLegacyId, targetLegacyId } =
      data.createPayment.entity;
    if (!paymentDate || !paymentLegacyId || !targetLegacyId) return;

    openWebViewPaymentModal(
      buildWebViewPaymentData(t, {
        amount: amount.toString(),
        isWallet: payment === PaymentMethod.Wallet,
        paymentDate,
        paymentId: paymentLegacyId,
        serviceId: PaymentService.Pay_invoice,
        serviceName,
        targetId: targetLegacyId,
      })
    );
    clearPaymentInvoice();
    onClose();
    refetchPaymentData();
    onSuccess();
  }

  function createTerminalInvoicePayment(invoice: PayableInvoice) {
    setTerminalInvoice(invoice);
    createTerminalPayment({
      variables: {
        serviceId: PaymentService.Pay_invoice,
        targetId: invoice.id,
      },
    });
  }

  function createInvoicePayment(invoice: PayableInvoice) {
    createPayment({
      variables: {
        serviceId: PaymentService.Pay_invoice,
        targetId: invoice.id,
        paymentMethod: payment,
        source,
        yigimTemplateGroup: getPaymentYigimTemplateGroup(
          isDesktop,
          isAppleGooglePayEnabled
        ),
        ...(payment === PaymentMethod.My_card && cardId && { cardId }),
      },
    });
  }

  async function payInvoice() {
    if (isProcessingPayment) return;
    shouldSkipNextPaymentFinished.current = false;

    if (!isWebView && !isTerminal) {
      openPaymentWindow();
    }

    const invoice = paymentInvoice.current ?? (await getInvoice());
    if (!invoice) {
      closePaymentWindow();
      return;
    }
    paymentInvoice.current = invoice;

    if (isTerminal) {
      createTerminalInvoicePayment(invoice);
      return;
    }
    createInvoicePayment(invoice);
  }

  function handleWebViewPostMessage(
    params: Request.Params[EventName.PaymentStatus]
  ) {
    if (!isOpen || isAddBalanceOpen || payment === PaymentMethod.My_card) {
      return;
    }
    const paymentParams = params as unknown as PaymentStatusParams;
    openWebViewPaymentModal(paymentParams);
    if (paymentParams.status !== PaymentStatus.Success) return;

    clearPaymentInvoice();
    onClose();
    sleep(getRefetchDelay(paymentProvider)).then(refetchPaymentData);
    onSuccess();
  }

  function handlePostMessage() {
    if (shouldSkipNextPaymentFinished.current) {
      shouldSkipNextPaymentFinished.current = false;
      return;
    }
    if (!isOpen || isAddBalanceOpen || payment === PaymentMethod.My_card) {
      return;
    }
    sleep(getRefetchDelay(paymentProvider)).then(finishPayment);
  }

  useWebViewPostMessage({
    eventName: EventName.PaymentStatus,
    handler: handleWebViewPostMessage,
  });
  usePostMessage({
    postMessageName: 'paymentFinished',
    handler: handlePostMessage,
  });

  return {
    cardId,
    clearCreatePaymentError,
    closeTooManyRequests: () => toggleTooManyRequests(false),
    createPaymentError,
    handleBalanceTopUpSuccess,
    isAddBalanceOpen,
    isAppleGooglePayEnabled,
    isDuplicate,
    isNotEnoughFundsOpen,
    isProcessingPayment,
    isTerminalOpen,
    isTooManyRequestsOpen,
    payInvoice,
    payment,
    paymentMethods,
    resetInvoice: clearPaymentInvoice,
    setCardId,
    setPayment,
    startAddBalance: () => {
      toggleNotEnoughFunds(false);
      toggleAddBalance(true);
    },
    terminalInvoice,
    toggleAddBalance,
    toggleNotEnoughFunds,
    toggleTerminal,
  };
}

export type InvoicePaymentController = ReturnType<typeof useInvoicePayment>;
